import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthState, LoginCredentials, RegisterData, SubscriptionPlan } from '../types/auth';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  updateSubscription: (plan: SubscriptionPlan['id']) => Promise<void>;
  hasFeature: (feature: string) => boolean;
  remainingGenerations: () => number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    // 模拟检查本地存储的认证状态
    const checkAuthStatus = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          setAuthState({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          setAuthState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Failed to check auth status:', error);
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));

      const mockUser: User = {
        id: '1',
        email: credentials.email,
        name: '测试用户',
        createdAt: new Date(),
        lastLoginAt: new Date(),
        subscription: {
          id: 'free',
          plan: 'free',
          status: 'active',
          currentPeriodStart: new Date(),
          currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          cancelAtPeriodEnd: false,
          features: {
            maxScriptsPerMonth: 3,
            maxCharacters: 5,
            hasAICharacterGeneration: false,
            hasAdvancedStoryboard: false,
            hasMultipleExportFormats: false,
            hasPrioritySupport: false,
          },
        },
      };

      localStorage.setItem('user', JSON.stringify(mockUser));

      setAuthState({
        user: mockUser,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      console.error('Login failed:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));

      const mockUser: User = {
        id: Date.now().toString(),
        email: data.email,
        name: data.name,
        createdAt: new Date(),
        lastLoginAt: new Date(),
        subscription: {
          id: 'free',
          plan: 'free',
          status: 'active',
          currentPeriodStart: new Date(),
          currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          cancelAtPeriodEnd: false,
          features: {
            maxScriptsPerMonth: 3,
            maxCharacters: 5,
            hasAICharacterGeneration: false,
            hasAdvancedStoryboard: false,
            hasMultipleExportFormats: false,
            hasPrioritySupport: false,
          },
        },
      };

      localStorage.setItem('user', JSON.stringify(mockUser));

      setAuthState({
        user: mockUser,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      console.error('Registration failed:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('user');
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  const updateSubscription = async (plan: SubscriptionPlan['id']) => {
    if (!authState.user) return;

    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));

      const updatedUser = {
        ...authState.user,
        subscription: {
          ...authState.user.subscription,
          plan,
          id: plan,
        },
      };

      localStorage.setItem('user', JSON.stringify(updatedUser));

      setAuthState(prev => ({
        ...prev,
        user: updatedUser,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Subscription update failed:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const hasFeature = (feature: string): boolean => {
    if (!authState.user?.subscription?.features) return false;
    const value = authState.user.subscription.features[feature as keyof typeof authState.user.subscription.features];
    return typeof value === 'boolean' ? value : false;
  };

  const remainingGenerations = (): number => {
    if (!authState.user?.subscription?.features) return 0;
    const max = authState.user.subscription.features.maxScriptsPerMonth;
    // 这里应该计算已使用的次数，现在返回固定值
    if (max === 'Infinity') return Infinity;
    return Math.max(0, max - 1);
  };

  const value: AuthContextType = {
    ...authState,
    login,
    register,
    logout,
    updateSubscription,
    hasFeature,
    remainingGenerations,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};